// react_frontend/src/Components/Modules/SupportWidget.js
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";

// RSuite Components
import {
  Button,
  Input,
  Loader,
} from "rsuite";
import "rsuite/dist/rsuite.min.css";

// Icons
import {
  FaTimes,
  FaPaperPlane,
  FaRobot,
  FaUser,
} from "react-icons/fa";

const SupportWidget = ({ apiUrl, onClose }) => {
  const [text, setText] = useState("");
  const [messages, setMessages] = useState([
    { from: "bot", text: "👋 Hi! Ask me anything about the Lumen." }
  ]);
  const [loading, setLoading] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const inactivityTimer = useRef(null);
  const messagesEndRef = useRef(null);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Inactivity timer (auto-close after 5 min)
  const resetInactivityTimer = () => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        { from: "bot", text: "⚠️ You have been inactive for 5 minutes. Chat closed." }
      ]);
      // We don't auto-close here — let the parent (Sidebar) handle visibility
      // So just reset messages after delay if needed, or leave it to parent
    }, 300000);
  };

  useEffect(() => {
    resetInactivityTimer();
    return () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, []);

  // Send message
  async function send() {
    if (!text.trim()) return;
    resetInactivityTimer();

    const userMsg = text;
    setMessages(prev => [...prev, { from: "user", text: userMsg }]);
    setText("");
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${apiUrl}/support/chat`,
        { message: userMsg },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev => [...prev, { from: "bot", text: res.data.answer }]);
    } catch (e) {
      console.error(e);
      setMessages(prev => [...prev, { from: "bot", text: "❌ Server error" }]);
    } finally {
      setLoading(false);
    }
  }

  // ✅ ALWAYS render the open chat — no closed state
  return (
    <div
      style={{
        position: "fixed",
        bottom: "0",
        right: "0",
        width: "340px",
        height: minimized ? "47px" : "420px",
        borderRight: "1px solid #4351a324",
        borderRadius: "12px 12px 0 0",
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        zIndex: 1050,
        overflow: "hidden",
        transition: "height 0.3s ease",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "#4351A3",
          color: "#fff",
          padding: "12px 16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #3a458a",
        }}
        onClick={() => minimized && setMinimized(false)}
      >
        <div className="d-flex align-items-center gap-2">
          <FaRobot /> <strong>Support Chat</strong>
        </div>
        <div>
          
        {/* Minimize button */}
        {!minimized ? (
          <Button
            appearance="subtle"
            type="button"
            onClick={() => setMinimized(true)}
            style={{ color: "white", padding: "4px", minWidth: "auto", marginRight: "8px" }}
            title="Minimize"
          >
            <span style={{ fontSize: "18px" }}>−</span> {/* Minus for minimize */}
          </Button>
        ) : null}
        {/* Close button will be handled by parent via state — but you can keep it */}
        <Button
          appearance="subtle"
          type="button"
          onClick={() => {
            onClose?.();
          }}
          style={{
            color: "white",
            padding: "4px",
            minWidth: "auto",
          }}
          title="Close chat"
        >
          <FaTimes />
        </Button>
        </div>
      </div>
      {/* Messages & Input — only show when NOT minimized */}
{!minimized && (
  <>
      {/* Messages */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "12px",
          backgroundColor: "#f8f9fa",
        }}
      >
        {messages.map((m, i) => (
          <div
            key={i}
            style={{
              textAlign: m.from === "user" ? "right" : "left",
              margin: "8px 0",
              display: "flex",
              justifyContent: m.from === "user" ? "flex-end" : "flex-start",
            }}
          >
            <div
              style={{
                display: "inline-block",
                padding: "10px 14px",
                borderRadius: "18px",
                background: m.from === "user" ? "#4351A3" : "#e9ecef",
                color: m.from === "user" ? "white" : "#333",
                maxWidth: "85%",
                wordWrap: "break-word",
                fontSize: "14px",
                lineHeight: "1.4",
              }}
            >
              {m.from === "bot" && <FaRobot className="me-1" style={{ fontSize: "0.9em" }} />}
              {m.from === "user" && <FaUser className="me-1" style={{ fontSize: "0.9em" }} />}
              <ReactMarkdown>{m.text}</ReactMarkdown>
            </div>
          </div>
        ))}
        {loading && (
          <div className="d-flex justify-content-start mt-2">
            <div
              style={{
                display: "inline-block",
                padding: "10px 14px",
                borderRadius: "18px",
                background: "#e9ecef",
                color: "#333",
                maxWidth: "85%",
                wordWrap: "break-word",
                fontSize: "14px",
                lineHeight: "1.4",
              }}
            >
              <Loader size="sm" /> Typing...
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div
        style={{
          display: "flex",
          padding: "12px",
          borderTop: "1px solid #ddd",
          backgroundColor: "white",
        }}
      >
        <Input
          value={text}
          onChange={setText}
          onKeyDown={(e) => e.key === "Enter" && !loading && send()}
          placeholder="Write your question..."
          style={{ flex: 1, borderRadius: "20px 0 0 20px", outline: 0 }}
          size="md"
        />
        <Button
          appearance="primary"
          color="blue"
          onClick={send}
          disabled={loading || !text.trim()}
          style={{
            borderRadius: "0 20px 20px 0",
            padding: "10px 16px",
            minWidth: "auto",
            backgroundColor: "#4351a3"
          }}
        >
          {loading ? <Loader size="sm" /> : <FaPaperPlane />}
        </Button>
      </div>
  </>
)}

      <style jsx>{`
        /* your existing styles */
      `}</style>
    </div>
  );
};

export default SupportWidget;